import React, { useContext, useEffect, useState } from 'react';
import { Divider, Group, Stack, Text } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { Trans } from '@lingui/react';
import { useParams } from 'react-router-dom';
import { IconRefresh } from '@tabler/icons';
import { CustomModalHeader } from '../../Dialogs';
import PanePaper from '../../PanePaper';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import BookitupThemeIcon from '../../../Atoms/BookitupThemeIcon';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { EventContext } from '../../../Service/Context/EventContext';
import { EventParticipationsContext } from '../../../Service/Context/EventParticipationContextV2';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { ToastS } from '../../../Service/ToastS';
import { fetchTaskGroups, fetchTasksByContactIds } from '../../../Service/restapi/taskService';
import { TaskGroup } from './TaskGroup';
import ParticipantTaskGroup from './ParticipantTaskGroup';

export default React.memo(() => {
  const { id: eventId } = useParams();
  const { profile, bookitupTeam } = useContext(ProfileContext);
  const { taskReloadTrigger } = useContext(EventContext);
  const { contactIds, contacts, loading: participantsLoading } = useContext(EventParticipationsContext);
  const [lastModified, setLastModified] = useState(new Date());
  const [taskGroups, setTaskGroups] = useState([]);
  const [participantTasks, setParticipantTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [participantTasksLoading, setParticipantTasksLoading] = useState(false);
  const { openContextModal, closeAll } = useModals();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { teamAddonPresent } = useBookitupSubscription();

  useEffect(() => {
    setLoading(true);
    fetchTaskGroups('events')
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((groups) => {
            setTaskGroups(groups);
          });
        } else {
          console.error('could not load taskLists for entityType events');
        }
      })
      .catch(() => {
        console.error('could not load taskLists for entityType events');
      })
      .finally(() => setLoading(false));
  }, [lastModified, taskReloadTrigger]);

  useEffect(() => {
    let active = true;

    if (contactIds.length === 0) {
      setParticipantTasks([]);
      setParticipantTasksLoading(false);
      return undefined;
    }

    setParticipantTasksLoading(true);
    fetchTasksByContactIds(contactIds, true)
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error('could not load general tasks assigned to event participants');
        }

        const tasks = await resp.json();
        if (active) {
          setParticipantTasks(tasks);
        }
      })
      .catch(() => {
        if (active) {
          setParticipantTasks([]);
          ToastS.error('fetch.event.participant.tasks', 'Aufgaben der Eventkontakte konnten nicht geladen werden');
        }
      })
      .finally(() => {
        if (active) {
          setParticipantTasksLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [contactIds, lastModified, taskReloadTrigger]);

  const participantTaskGroups = contactIds
    .map((contactId) => ({
      contact: contacts.find((contact) => contact.id === contactId),
      tasks: participantTasks.filter((task) => task.contactId === contactId),
    }))
    .filter(({ contact, tasks }) => contact && tasks.length > 0);

  const tasksLoading = loading || participantsLoading || participantTasksLoading;

  const openTaskCreateModal = () =>
    openContextModal('taskCreateModal', {
      title: (
        <CustomModalHeader>
          <Trans id="task.create" />
        </CustomModalHeader>
      ),
      innerProps: {
        profile,
        bookitupTeam,
        teamAddonPresent,
        entityId: eventId,
        entityType: 'events',
        finish: () => {
          closeAll();
          setLastModified(new Date());
        },
      },
      centered: true,
    });

  return (
    <PanePaper
      withShadow
      withoutBorder
      title="Aufgaben"
      tooltip="help.event.tasks.pane"
      rightSection={
        <Group spacing="xs">
          {!tasksLoading && (
            <BookitupThemeIcon onClick={() => setLastModified(new Date())}>
              <IconRefresh />
            </BookitupThemeIcon>
          )}
          <BookitupAddIcon
            onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.SAVE_TASK, openTaskCreateModal)}
          />
        </Group>
      }
    >
      {tasksLoading && <SkeletonItems height={50} />}
      {!tasksLoading && taskGroups.length === 0 && participantTasks.length === 0 && (
        <Text italic color="dimmed" size="xs" weight="lighter">
          Noch nichts hinzugefügt.
        </Text>
      )}
      {!tasksLoading && (taskGroups.length > 0 || participantTaskGroups.length > 0) && (
        <Stack spacing={5}>
          {taskGroups.map((group) => (
            <TaskGroup key={group.id} group={group} entityId={eventId} lastModified={lastModified} />
          ))}
          {participantTaskGroups.length > 0 && <Divider label="Kontakte" labelPosition="center" my="xs" />}
          {participantTaskGroups.map(({ contact, tasks }) => (
            <ParticipantTaskGroup
              key={contact.id}
              contact={contact}
              tasks={tasks}
              onTaskChange={(updatedTask) =>
                setParticipantTasks((currentTasks) =>
                  currentTasks.map((currentTask) => (currentTask.id === updatedTask.id ? updatedTask : currentTask)),
                )
              }
            />
          ))}
        </Stack>
      )}
    </PanePaper>
  );
});
